<template>
  <div
    ref="track"
    class="hd-range-slider"
    @pointerdown="onTrackPointerDown"
  >
    <div class="hd-range-slider__rail"></div>
    <div class="hd-range-slider__fill" :style="fillStyle"></div>
    <button
      type="button"
      class="hd-range-slider__handle"
      :style="lowHandleStyle"
      role="slider"
      :aria-valuemin="min"
      :aria-valuemax="high - minRange"
      :aria-valuenow="low"
      @pointerdown.stop="onHandlePointerDown('low', $event)"
      @keydown="onHandleKeydown('low', $event)"
    ></button>
    <button
      type="button"
      class="hd-range-slider__handle"
      :style="highHandleStyle"
      role="slider"
      :aria-valuemin="low + minRange"
      :aria-valuemax="max"
      :aria-valuenow="high"
      @pointerdown.stop="onHandlePointerDown('high', $event)"
      @keydown="onHandleKeydown('high', $event)"
    ></button>
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import {
  percentToValue,
  resolveHandleValue,
  valueToPercent
} from './range_slider_utils';

// A local, dual-handle range slider replacing vue-slider-component (Vue-2 only).
// Fully controlled: it reads `value` ([low, high]) and emits `input` with a new
// pair, so v-model callers drive it.
export default defineComponent({
  name: 'HdRangeSlider',

  props: {
    // `modelValue`/`update:modelValue` is what `v-model` uses; `value`/`input` is
    // kept because a number of call sites bind those explicitly rather than
    // through v-model. Both are live Vue 3 APIs, not a compat leftover.
    value: {
      type: Array,
      default: null
    },
    modelValue: {
      type: Array,
      default: undefined
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    interval: {
      type: Number,
      default: 1
    },
    minRange: {
      type: Number,
      default: 0
    },
    // Accepted for API parity with vue-slider-component; every caller disables
    // tooltips, so none are rendered.
    tooltip: {
      type: [Boolean, String],
      default: false
    }
  },

  emits: ['input', 'update:modelValue'],

  setup(props, { emit }) {
    const track = ref(null);

    const pair = computed(() => {
      const value = props.modelValue !== undefined ? props.modelValue : props.value;

      return Array.isArray(value) && value.length === 2
        ? value
        : [props.min, props.max];
    });

    const low = computed(() => pair.value[0]);
    const high = computed(() => pair.value[1]);

    const setHandle = (handle, rawValue) => {
      const resolved = resolveHandleValue({
        handle,
        value: rawValue,
        low: low.value,
        high: high.value,
        min: props.min,
        max: props.max,
        step: props.interval,
        minRange: props.minRange
      });

      let next = null;
      if (handle === 'low' && resolved !== low.value) {
        next = [resolved, high.value];
      } else if (handle === 'high' && resolved !== high.value) {
        next = [low.value, resolved];
      }

      if (next) {
        emit('input', next);
        emit('update:modelValue', next);
      }
    };

    const valueFromClientX = (clientX) => {
      const rect = track.value.getBoundingClientRect();
      const percent = rect.width ? ((clientX - rect.left) / rect.width) * 100 : 0;

      return percentToValue(percent, props.min, props.max);
    };

    let activeHandle = null;

    const onPointerMove = (event) => {
      if (activeHandle) setHandle(activeHandle, valueFromClientX(event.clientX));
    };

    const stopDrag = () => {
      activeHandle = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDrag);
    };

    const onHandlePointerDown = (handle, event) => {
      activeHandle = handle;
      if (event.target.setPointerCapture) {
        event.target.setPointerCapture(event.pointerId);
      }
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopDrag);
    };

    // Clicking the rail moves whichever handle is nearest.
    const onTrackPointerDown = (event) => {
      const value = valueFromClientX(event.clientX);
      const handle = Math.abs(value - low.value) <= Math.abs(value - high.value)
        ? 'low'
        : 'high';

      setHandle(handle, value);
    };

    const onHandleKeydown = (handle, event) => {
      const step = props.interval || 1;
      let delta = 0;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -step;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = step;
      else return;

      event.preventDefault();
      const current = handle === 'low' ? low.value : high.value;
      setHandle(handle, current + delta);
    };

    onBeforeUnmount(stopDrag);

    const lowPercent = computed(() => valueToPercent(low.value, props.min, props.max));
    const highPercent = computed(() => valueToPercent(high.value, props.min, props.max));

    return {
      track,
      low,
      high,
      onHandlePointerDown,
      onTrackPointerDown,
      onHandleKeydown,
      lowHandleStyle: computed(() => ({ left: `${lowPercent.value}%` })),
      highHandleStyle: computed(() => ({ left: `${highPercent.value}%` })),
      fillStyle: computed(() => ({
        left: `${lowPercent.value}%`,
        width: `${highPercent.value - lowPercent.value}%`
      }))
    };
  }
});
</script>

<style>
.hd-range-slider {
  position: relative;
  height: 20px;
  margin: 0 8px;
  cursor: pointer;
  touch-action: none;
}

.hd-range-slider__rail {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 2px;
  background-color: #dbe0e3;
}

.hd-range-slider__fill {
  position: absolute;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 2px;
  background-color: #007bff;
}

.hd-range-slider__handle {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  transform: translate(-50%, -50%);
  border: 1px solid #c3ccd2;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: grab;
}

.hd-range-slider__handle:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.35);
}

.hd-range-slider__handle:active {
  cursor: grabbing;
}
</style>
